/**
 * Mục "Tiền cọc" trong tờ chi tiết lịch (N2 — docs/plans/pv2-n2-coc.md).
 *
 * SAPA không giữ tiền: tiền cọc vào thẳng tài khoản của tiệm, quầy chỉ XÁC NHẬN đã thấy tiền. Bốn việc:
 *  - "Đã nhận cọc" (quyền thu tiền): lịch đang chờ cọc → được xác nhận, khách nhận tin. Chỉ khi lịch
 *    CHƯA giữ cọc nào (soát N2: hai quầy cùng bấm → sổ giữ gấp đôi).
 *  - "Sửa cọc ghi nhầm" (quyền hoàn / đảo tiền): bỏ lần ghi nhận gần nhất bằng dòng đảo, rồi ghi lại số đúng.
 *  - "Hoàn cọc" (quyền hoàn / đảo tiền): trả lại toàn bộ cọc đang giữ, bắt buộc lý do.
 *  - "Giữ cọc" (khách không đến / khách tự huỷ): cọc thành hoá đơn "Tiền cọc giữ lại", bắt buộc lý do.
 * Cọc còn giữ khi khách tới làm thì tự trừ vào hoá đơn lúc thu tiền — không cần bấm gì.
 */
import { useState } from 'react'
import { type Api, ApiFailure, type DayAppointment, tienVnd } from './api.ts'
import { OLyDo, OTien, useKhoaIdem } from './GoiVi.tsx'
import { T } from './text/vi.ts'

type Cach = 'transfer' | 'cash'
type Viec = 'nhan' | 'hoan' | 'giu' | 'dao'

/**
 * Mục Tiền cọc còn VIỆC để bấm không (nút ghi nhận / giữ / hoàn / sửa)? Màn Lịch dùng chung để không in thêm câu "Lịch này đã xong,
 * không còn việc gì phải làm" ngay trên một nút bấm (ui-critic vòng 69).
 */
export function tinhViecCoc(lich: DayAppointment, choNhan: boolean, choHoan: boolean) {
  const dangGiu = BigInt(lich.cocVnd ?? '0')
  const choCoc = lich.status === 'awaiting_deposit'
  const daKetThuc = ['cancelled', 'no_show', 'void'].includes(lich.status)
  // Nhận cọc chỉ khi lịch chưa giữ đồng nào: chờ cọc, còn hẹn, hoặc đã hết giờ giữ mà tiền tới muộn. Lịch ĐÃ HUỶ mà từng phải
  // cọc (có số cọc chốt) cũng ghi sổ được: tiệm nghỉ → quầy huỷ lịch chờ cọc → khách đã chuyển tiền thật → ghi sổ rồi Hoàn cọc
  // (K2 vòng sửa 2). GIỮ cọc chỉ khi khách không đến hoặc huỷ TRỄ hạn huỷ; tiệm huỷ, khách huỷ đúng hạn, tiệm nghỉ → máy chủ từ chối
  // (409), chỉ còn hoàn (vòng sửa 3).
  const daHuyTungCoc = lich.status === 'cancelled' && lich.cocCanVnd != null
  const nhanDuoc =
    choNhan &&
    dangGiu <= 0n &&
    (choCoc || lich.status === 'booked' || lich.status === 'void' || daHuyTungCoc)
  const giuDuoc =
    choHoan && dangGiu > 0n && (lich.status === 'no_show' || lich.status === 'cancelled')
  return {
    dangGiu,
    choCoc,
    daKetThuc,
    daHuyTungCoc,
    nhanDuoc,
    giuDuoc,
    coViec: nhanDuoc || giuDuoc || (choHoan && dangGiu > 0n),
  }
}

export function MucCoc({
  api,
  lich,
  soTienMacDinh,
  choNhan,
  choHoan,
  onDaDoi,
  onLoi,
}: {
  api: Api
  lich: DayAppointment
  /** Số tiền cọc tiệm cài (điền sẵn); `null` = tiệm chưa cài. */
  soTienMacDinh: string | null
  choNhan: boolean
  choHoan: boolean
  onDaDoi: (thongBao: string) => void
  onLoi: (s: string) => void
}) {
  const { dangGiu, choCoc, daKetThuc, daHuyTungCoc, nhanDuoc, giuDuoc } = tinhViecCoc(
    lich,
    choNhan,
    choHoan,
  )
  const [viec, setViec] = useState<Viec | null>(choCoc && nhanDuoc ? 'nhan' : null)
  // Số chốt trên lịch lúc đặt (0041) thắng số cấu hình hiện tại — chủ tiệm có thể đã đổi mức (RETRO M3).
  const [soTien, setSoTien] = useState(lich.cocCanVnd ?? soTienMacDinh ?? '')
  const [cach, setCach] = useState<Cach>('transfer')
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  const [khoa, lamMoiKhoa] = useKhoaIdem()

  if (!choCoc && dangGiu <= 0n && !nhanDuoc) return null

  async function chay(fn: () => Promise<string>) {
    setDang(true)
    try {
      const bao = await fn()
      lamMoiKhoa()
      onDaDoi(bao)
    } catch (e) {
      // Mất mạng: GIỮ khoá — bấm lại là đúng yêu cầu cũ, không ghi sổ hai lần.
      if (!(e instanceof ApiFailure) || e.code !== 'OFFLINE') lamMoiKhoa()
      onLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDang(false)
    }
  }

  const soTienHopLe = soTien !== '' && BigInt(soTien) > 0n && BigInt(soTien) % 1000n === 0n
  const coLyDo = lyDo.trim().length >= 2
  const giuTien = tienVnd(dangGiu.toString())

  return (
    <div className="muc-coc">
      <b className="nhan">{T.depositTitle}</b>
      {choCoc && <p className="chu-phu">{T.depositWaiting}</p>}
      {/* Đọc trước mọi nút: "Chưa nhận cọc" (+ chú thích lịch đã huỷ) nằm TRÊN ô nhập / nút "Đã nhận cọc", không tụt xuống dưới. */}
      {!choCoc && dangGiu <= 0n && <p className="chu-phu">{T.depositNone}</p>}
      {daHuyTungCoc && nhanDuoc && <p className="chu-phu">{T.depositCancelledHint}</p>}
      {dangGiu > 0n && (
        <p>
          {T.depositHolding(giuTien)}
          {!daKetThuc && <span className="chu-phu"> · {T.depositAutoApply}</span>}
        </p>
      )}

      {viec === 'nhan' && nhanDuoc && (
        <div className="cot-nhap">
          <label className="nhan" htmlFor="so-coc">
            {T.depositAmount}
          </label>
          <OTien id="so-coc" giaTri={soTien} onDoi={setSoTien} />
          <ChonCach cach={cach} onDoi={setCach} />
          <button
            type="button"
            className="nut nut-chinh nut-rong"
            disabled={dang || !soTienHopLe}
            onClick={() =>
              void chay(async () => {
                await api.coc.nhan(lich.appointmentId, { soTienVnd: soTien, cach }, khoa)
                return T.depositReceived(tienVnd(soTien))
              })
            }
          >
            {T.depositReceive}
          </button>
        </div>
      )}

      {viec === 'hoan' && choHoan && (
        <div className="cot-nhap">
          <ChonCach cach={cach} onDoi={setCach} />
          <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={T.depositRefundReasonHint} />
          <button
            type="button"
            className="nut nut-nguy nut-rong"
            disabled={dang || !coLyDo}
            onClick={() =>
              void chay(async () => {
                const r = await api.coc.hoan(
                  lich.appointmentId,
                  { cach, lyDo: lyDo.trim(), duKienVnd: dangGiu.toString() },
                  khoa,
                )
                return T.depositRefunded(tienVnd(r.daHoanVnd))
              })
            }
          >
            {T.depositRefundConfirm(giuTien)}
          </button>
        </div>
      )}

      {viec === 'giu' && giuDuoc && (
        <div className="cot-nhap">
          <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={T.depositKeepReasonHint} />
          <button
            type="button"
            className="nut nut-chinh nut-rong"
            disabled={dang || !coLyDo}
            onClick={() =>
              void chay(async () => {
                const r = await api.coc.giu(lich.appointmentId, lyDo.trim(), khoa)
                return T.depositKept(r.hoaDon.number, tienVnd(r.hoaDon.totalVnd))
              })
            }
          >
            {T.depositKeepConfirm(giuTien)}
          </button>
        </div>
      )}

      {viec === 'dao' && choHoan && (
        <div className="cot-nhap">
          <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={T.depositReverseHint} />
          <button
            type="button"
            className="nut nut-nguy nut-rong"
            disabled={dang || !coLyDo}
            onClick={() =>
              void chay(async () => {
                const r = await api.coc.dao(lich.appointmentId, lyDo.trim(), khoa)
                return T.depositReversed(tienVnd(r.daDaoVnd))
              })
            }
          >
            {T.depositReverseConfirm(giuTien)}
          </button>
        </div>
      )}

      {/* Chưa có đồng nào: nói thẳng trạng thái — nút trơn "Đã nhận cọc" từng bị đọc thành trạng thái (ui-critic v21). */}
      {viec === null && nhanDuoc && (
        <button type="button" className="nut nut-vien nut-rong" onClick={() => setViec('nhan')}>
          {T.depositReceiveCta}
        </button>
      )}
      {viec === null && (
        <div className="hang-phu">
          {giuDuoc && (
            <button type="button" className="nut nut-nhat" onClick={() => setViec('giu')}>
              {T.depositKeep}
            </button>
          )}
          {choHoan && dangGiu > 0n && (
            <button type="button" className="nut nut-nhat" onClick={() => setViec('hoan')}>
              {T.depositRefund}
            </button>
          )}
          {choHoan && dangGiu > 0n && (
            <button type="button" className="nut nut-nhat" onClick={() => setViec('dao')}>
              {T.depositReverse}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function ChonCach({ cach, onDoi }: { cach: Cach; onDoi: (c: Cach) => void }) {
  return (
    <div className="hang-chip">
      {(['transfer', 'cash'] as const).map((c) => (
        <button
          key={c}
          type="button"
          className={`chip ${cach === c ? 'dang-chon' : ''}`}
          aria-pressed={cach === c}
          onClick={() => onDoi(c)}
        >
          {c === 'transfer' ? T.depositByTransfer : T.depositByCash}
        </button>
      ))}
    </div>
  )
}
